import { FC, useMemo } from "react";
import Loading, { SpinnerSize } from "../Loading";
import { getIconFromStringOrElement } from "../Icons/utils";
import Truncated from "../Truncated";
import { IconProps } from "../Icon/types";
import { getChildrenTextContent } from "../utils";
import { ButtonProps } from "./types";
import { styles } from "./styles";

/**
 * `Button` is an interactive element activated by a user and can be linked to perform a programmable action.
 */
const Button: FC<ButtonProps> = ({
  variant = "primary",
  className = "",
  ariaLabel: ariaLabelProp,
  actionType = "button",
  tabIndex = 0,
  name,
  prefix,
  suffix,
  children,
  disabled = false,
  stopPropagation = false,
  isLoading = false,
  "aria-expanded": ariaExpanded,
  size = "medium",
  truncated = false,
  "data-testid": dataTestId = "kit-Button",
  onClick
}) => {
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (stopPropagation) {
      event.stopPropagation();
    }
    if (isLoading || disabled) return;
    onClick?.(event);
  };

  const iconProps: Partial<IconProps> = useMemo(
    () => (size === "small" ? { size: "extra-small" } : {}),
    [size]
  );

  const PrefixIcon = useMemo(
    () => getIconFromStringOrElement(prefix, iconProps),
    [prefix, size]
  );
  const SuffixIcon = useMemo(
    () => getIconFromStringOrElement(suffix, iconProps),
    [suffix, size]
  );

  // Use button text as aria-label during loading state because no text is displayed in that state.
  const ariaLabel = useMemo(
    () =>
      ariaLabelProp ||
      (isLoading && !disabled ? getChildrenTextContent(children) : undefined),
    [ariaLabelProp, isLoading, disabled, children]
  );

  const truncatedProps = useMemo(() => {
    if (!truncated) {
      return undefined;
    }
    return {
      text: getChildrenTextContent(children),
      lines: 1,
      ...(truncated === true ? {} : truncated)
    };
  }, [truncated, children]);

  return (
    <button
      css={[
        styles.base,
        styles.sizes[size],
        styles.variants[variant],
        isLoading && !disabled && styles.loading,
        children && styles.normalizeLineHeight
      ]}
      name={name}
      data-testid={dataTestId}
      aria-label={ariaLabel}
      aria-expanded={ariaExpanded}
      aria-busy={isLoading}
      data-loading={isLoading}
      type={actionType}
      onClick={handleClick}
      disabled={disabled || isLoading}
      className={className}
      tabIndex={tabIndex}
    >
      {PrefixIcon}
      {truncatedProps ? <Truncated {...truncatedProps} /> : children}
      {SuffixIcon}

      {isLoading && !disabled && (
        <span className="kit-Button-loading">
          <Loading.Spinner
            size={size === "small" ? SpinnerSize.TINY : SpinnerSize.SMALL}
          />
        </span>
      )}
    </button>
  );
};

export default Button;
export * from "./types";
